import React, { useState, useEffect, useCallback } from "react";
import {
  PlusIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  TrashIcon,
  PencilIcon,
  UsersIcon,
  ShieldCheckIcon,
  XIcon,
} from "lucide-react";
import api from "../services/api";

interface GroupNode {
  id: string;
  name: string;
  children: GroupNode[];
}

interface RoleAssignment {
  assignment_id: string;
  user_id: number;
  username: string;
  role_id: string;
  role_name: string;
  scope_type: string;
}

const TreeNode: React.FC<{
  node: GroupNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}> = ({ node, depth, selectedId, onSelect }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <button
        onClick={() => onSelect(node.id)}
        className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
          selectedId === node.id
            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        }`}
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
      >
        {hasChildren ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="mr-1 cursor-pointer"
          >
            {expanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
        ) : (
          <span className="w-5" />
        )}
        <span className="truncate">{node.name}</span>
      </button>
      {expanded &&
        node.children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
};

export const Groups: React.FC = () => {
  const [hierarchy, setHierarchy] = useState<GroupNode[]>([]);
  const [flatGroups, setFlatGroups] = useState<
    Array<{ id: string; name: string; parent_id: string | null }>
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formParentId, setFormParentId] = useState("");
  const [assignUserId, setAssignUserId] = useState("");
  const [assignRoleId, setAssignRoleId] = useState("");
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [allUsers, setAllUsers] = useState<Array<{ id: number; username: string; full_name: string | null }>>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [hier, flat, rolesData, users] = await Promise.all([
        api.getGroupHierarchy(),
        api.getGroups(),
        api.getRoles(),
        api.getUsers(),
      ]);
      setHierarchy(hier);
      setFlatGroups(flat);
      setRoles(rolesData);
      setAllUsers(users);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchRoleAssignments = useCallback(async (groupId: string) => {
    try {
      const assignments = await api.getGroupRoleAssignments(groupId);
      setRoleAssignments(assignments);
    } catch (err) {
      console.error("Failed to fetch role assignments:", err);
      setRoleAssignments([]);
    }
  }, []);

  useEffect(() => {
    if (selectedGroupId) fetchRoleAssignments(selectedGroupId);
    else setRoleAssignments([]);
  }, [selectedGroupId, fetchRoleAssignments]);

  const handleCreateGroup = async () => {
    try {
      await api.createGroup({
        name: formName,
        description: formDescription || undefined,
        parent_id: formParentId || undefined,
      });
      setShowCreateModal(false);
      setFormName("");
      setFormDescription("");
      setFormParentId("");
      fetchData();
    } catch (err) {
      console.error("Failed to create group:", err);
    }
  };

  const handleEditGroup = async () => {
    if (!selectedGroupId) return;
    try {
      await api.updateGroup(selectedGroupId, {
        name: formName || undefined,
        description: formDescription || undefined,
      });
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      console.error("Failed to update group:", err);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;
    if (!window.confirm("Delete this group? This cannot be undone.")) return;
    try {
      await api.deleteGroup(selectedGroupId);
      setSelectedGroupId(null);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to delete group");
    }
  };

  const handleAssignRole = async () => {
    if (!selectedGroupId || !assignUserId || !assignRoleId) return;
    try {
      await api.assignUserRole(parseInt(assignUserId), {
        role_id: assignRoleId,
        scope_type: "group",
        scope_id: selectedGroupId,
      });
      setShowAssignModal(false);
      setAssignUserId("");
      setAssignRoleId("");
      fetchRoleAssignments(selectedGroupId);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to assign role");
    }
  };

  const handleRemoveAssignment = async (assignment: RoleAssignment) => {
    if (!selectedGroupId) return;
    try {
      await api.removeUserRole(assignment.user_id, assignment.assignment_id);
      fetchRoleAssignments(selectedGroupId);
    } catch (err) {
      console.error("Failed to remove assignment:", err);
    }
  };

  const selectedGroup = flatGroups.find((g) => g.id === selectedGroupId);

  // Group users by username for display
  const userRoleMap = new Map<string, RoleAssignment[]>();
  roleAssignments.forEach((a) => {
    const existing = userRoleMap.get(a.username) || [];
    existing.push(a);
    userRoleMap.set(a.username, existing);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading groups...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Groups
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Group
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tree */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Hierarchy
          </h2>
          {hierarchy.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No groups yet. Create one to get started.
            </p>
          ) : (
            hierarchy.map((root) => (
              <TreeNode
                key={root.id}
                node={root}
                depth={0}
                selectedId={selectedGroupId}
                onSelect={setSelectedGroupId}
              />
            ))
          )}
        </div>

        {/* Detail + Role Assignments */}
        <div className="lg:col-span-2 space-y-6">
          {selectedGroup ? (
            <>
              {/* Group info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedGroup.name}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setFormName(selectedGroup.name);
                        setFormDescription("");
                        setShowEditModal(true);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleDeleteGroup}
                      className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedGroup.parent_id ? "Child group" : "Root group (organization)"}
                </p>
              </div>

              {/* Users & Roles in this group */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <UsersIcon className="h-4 w-4 inline mr-1" />
                    Users in this group ({userRoleMap.size})
                  </h3>
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Assign Role
                  </button>
                </div>

                {userRoleMap.size === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No users have roles in this group yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {Array.from(userRoleMap.entries()).map(([username, assignments]) => (
                      <div
                        key={username}
                        className="px-4 py-3 bg-gray-50 dark:bg-gray-900 rounded-md"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {username}
                          </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-2">
                          {assignments.map((a) => (
                            <span
                              key={a.assignment_id}
                              className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            >
                              <ShieldCheckIcon className="h-3 w-3" />
                              {a.role_name}
                              <button
                                onClick={() => handleRemoveAssignment(a)}
                                className="ml-0.5 text-blue-500 hover:text-red-500"
                              >
                                <XIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-200 dark:border-gray-700">
              <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Select a group to view its users and role assignments.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Create Group
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Parent Group
                </label>
                <select
                  value={formParentId}
                  onChange={(e) => setFormParentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                >
                  <option value="">None (root)</option>
                  {flatGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!formName.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Group
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleEditGroup}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Assign Role in {selectedGroup?.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  User
                </label>
                <select
                  value={assignUserId}
                  onChange={(e) => setAssignUserId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                >
                  <option value="">Select a user</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username}{u.full_name ? ` (${u.full_name})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={assignRoleId}
                  onChange={(e) => setAssignRoleId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                >
                  <option value="">Select a role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignUserId("");
                  setAssignRoleId("");
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRole}
                disabled={!assignUserId || !assignRoleId}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groups;
